import { useHead } from '@vueuse/head';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { APP_URLS, SIS_COMPANY_URL } from '@/constants';

const DEFAULT_TITLE = 'SIS NFL Draft';
const DEFAULT_DESCRIPTION =
  'Sports Info Solutions (SIS) NFL Draft Site – Big Board, Draft Matrix, player stats, and draft analysis.';
const SITE_NAME = 'SIS NFL Draft';

/** Base URL for canonical, OG, and sitemap. Prefer env so staging can use its own URL. */
const getBaseUrl = (): string => {
  const envBase = import.meta.env?.VITE_APP_BASE_URL as string | undefined;
  return envBase && envBase.length > 0 ? envBase.replace(/\/$/, '') : APP_URLS.PROD;
};

/**
 * Origins where the app is deployed. When the page is served from one of these,
 * we use the current origin for canonical/og:url so Google's canonical matches the crawled URL.
 */
const KNOWN_ORIGINS = [
  APP_URLS.PROD,
  APP_URLS.TEST,
  APP_URLS.DEV,
  'https://www.nfldraft.sportsinfosolutions.com', // www variant of prod
];

/**
 * Base URL to use for canonical and og:url. Uses the current page origin when
 * the site is served from a known deployment so "canonical" matches the URL
 * Google crawls (avoids "Google chose different canonical than user").
 */
function getCanonicalBaseUrl(): string {
  if (typeof window === 'undefined') return getBaseUrl();
  const origin = window.location?.origin?.replace(/\/$/, '') ?? '';
  return KNOWN_ORIGINS.includes(origin) ? origin : getBaseUrl();
}

/** Default share image (absolute URL). */
const getDefaultOgImage = (baseUrl: string): string => `${baseUrl}/images/SIS_Logo-Red.png`;

export interface ISeoRouteMeta {
  title?: string;
  description?: string;
}

/**
 * Central SEO head: title, description, canonical, Open Graph, Twitter Card.
 * Call once in App.vue; reads route.meta and updates head on navigation.
 */
export function useSeoHead() {
  const route = useRoute();

  const head = computed(() => {
    const canonicalBase = getCanonicalBaseUrl();
    const meta = (route.meta ?? {}) as ISeoRouteMeta;
    const title = meta.title ?? DEFAULT_TITLE;
    const description = meta.description ?? DEFAULT_DESCRIPTION;
    const path =
      route.path === '/' ? '/home' : route.fullPath.replace(/#.*$/, '').replace(/\/$/, '') || '/home';
    const canonicalUrl = `${canonicalBase}${path}`;
    const ogImage = getDefaultOgImage(canonicalBase);

    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Sports Info Solutions',
      url: SIS_COMPANY_URL,
    };

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: canonicalBase,
      publisher: { '@id': `${SIS_COMPANY_URL}#organization` },
    };

    return {
      title: title.includes('|') ? title : `${title} | ${SITE_NAME}`,
      meta: [
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:type', content: 'website' },
        { property: 'og:image', content: ogImage },
        { property: 'og:site_name', content: SITE_NAME },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage },
      ],
      link: [{ rel: 'canonical', href: canonicalUrl }],
      script: [
        { type: 'application/ld+json', innerHTML: JSON.stringify(organizationSchema) },
        { type: 'application/ld+json', innerHTML: JSON.stringify(websiteSchema) },
      ],
    };
  });

  useHead(head);
}
